import React, { useEffect, useState } from "react";
import YhModal from "../../../../ui/YhModal";
import YhSelect, { buildStageSelectGroups } from "../../../../ui/YhSelect";
import YhButton from "../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import { changeStage } from "./ShowAllService";

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
  leadIds: string[];
  /** Parent stages with sub_Stage_name (same shape as All Leads stage filter) */
  stageTree?: any[];
  /** Flat fallback labels if tree missing */
  stageLabels?: string[];
};

const BulkStageModal = ({
  open,
  onClose,
  onUpdated,
  leadIds,
  stageTree = [],
  stageLabels = [],
}: Props) => {
  const [stage, setStage] = useState("");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");

  useEffect(() => {
    if (!open) return;
    setStage("");
    setProgress("");
  }, [open]);

  const groups = buildStageSelectGroups(stageTree || []);
  const flatOptions =
    groups.length > 0
      ? []
      : (stageLabels || [])
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .filter(
            (label, i, arr) =>
              arr.findIndex((x) => x.toLowerCase() === label.toLowerCase()) ===
              i
          )
          .map((label) => ({ label, value: label }));

  const onSave = async () => {
    if (!leadIds.length) {
      openErrorNotification("Select at least one lead");
      return;
    }
    if (!stage) {
      openErrorNotification("Select a stage");
      return;
    }

    const isCustomer = String(stage).trim().toLowerCase() === "customer";
    if (
      isCustomer &&
      !window.confirm(
        `Move ${leadIds.length} lead(s) to Customer? They will leave active All Leads.`
      )
    ) {
      return;
    }

    setSaving(true);
    const outcomes: boolean[] = [];
    try {
      const chunkSize = 8;
      for (let i = 0; i < leadIds.length; i += chunkSize) {
        const chunk = leadIds.slice(i, i + chunkSize);
        setProgress(
          `Updating ${Math.min(i + chunk.length, leadIds.length)} / ${
            leadIds.length
          }…`
        );
        const results = await Promise.all(
          chunk.map(async (leadId) => {
            try {
              const res = await changeStage({ id: leadId, stage });
              return res?.success === 1 || res?.success === true;
            } catch (_) {
              return false;
            }
          })
        );
        outcomes.push(...results);
      }

      const ok = outcomes.filter(Boolean).length;
      const fail = outcomes.length - ok;
      if (ok === 0) {
        openErrorNotification(
          fail ? `Failed to update ${fail} lead(s)` : "Bulk stage update failed"
        );
        return;
      }
      openSuccessNotification(
        fail
          ? `Updated stage for ${ok} lead(s), ${fail} failed`
          : `Updated stage to “${stage}” for ${ok} lead(s)`
      );
      onUpdated?.();
      onClose();
    } catch (e: any) {
      openErrorNotification(e?.message || "Unable to update stages");
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  return (
    <YhModal
      open={open}
      onClose={onClose}
      title="Bulk update stage"
      width={480}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-sm text-[#6B7280]">
          Selected leads:{" "}
          <span className="font-semibold text-[#111827]">{leadIds.length}</span>
          . Choose one stage to apply to all selected leads.
        </p>

        <YhSelect
          label="Stage"
          searchable
          searchPlaceholder="Search stages..."
          clearable
          disabled={saving}
          value={stage}
          placeholder="Select stage"
          groups={groups.length ? groups : undefined}
          options={groups.length ? undefined : flatOptions}
          onChange={(e: any) => setStage(String(e.target.value || ""))}
        />

        {progress ? (
          <p className="text-xs font-medium text-[#A02448]">{progress}</p>
        ) : null}

        <div className="mt-2 flex justify-end gap-2">
          <YhButton variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </YhButton>
          <YhButton
            onClick={onSave}
            disabled={saving || !leadIds.length || !stage}
          >
            {saving
              ? "Updating…"
              : `Update ${leadIds.length || ""} lead${
                  leadIds.length === 1 ? "" : "s"
                }`}
          </YhButton>
        </div>
      </div>
    </YhModal>
  );
};

export default BulkStageModal;
